import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import Lantern from "../components/Lantern";

export const Login = () => {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to keep sending love home.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field id="email" label="Email" type="email" value={email} onChange={setEmail} testid="login-email" />
        <Field id="password" label="Password" type="password" value={password} onChange={setPassword} testid="login-password" />
        <Button type="submit" disabled={busy} className="btn-primary w-full h-11 rounded-md" data-testid="login-submit-btn">
          {busy ? "Signing in…" : "Sign in"}
        </Button>
        <div className="text-sm text-[var(--ra-muted)] text-center">
          New here? <Link to="/register" className="text-[var(--ra-primary)] font-semibold underline-offset-4 hover:underline" data-testid="login-to-register">Create an account</Link>
        </div>
      </form>
    </AuthShell>
  );
};

export const Register = () => {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", mobile: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success("Account created — let's verify your identity next.");
      nav("/kyc");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Create your account" subtitle="It takes less than 2 minutes.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <Field id="full_name" label="Full name" value={form.full_name} onChange={set("full_name")} testid="register-fullname" />
        <Field id="email" label="Email" type="email" value={form.email} onChange={set("email")} testid="register-email" />
        <Field id="mobile" label="Mobile number" type="tel" value={form.mobile} onChange={set("mobile")} placeholder="+1 555 000 1234" testid="register-mobile" />
        <Field id="password" label="Password" type="password" value={form.password} onChange={set("password")} testid="register-password" />
        <Button type="submit" disabled={busy} className="btn-primary w-full h-11 rounded-md" data-testid="register-submit-btn">
          {busy ? "Creating…" : "Create account"}
        </Button>
        <div className="text-sm text-[var(--ra-muted)] text-center">
          Already a member? <Link to="/login" className="text-[var(--ra-primary)] font-semibold underline-offset-4 hover:underline" data-testid="register-to-login">Sign in</Link>
        </div>
      </form>
    </AuthShell>
  );
};

const Field = ({ id, label, type = "text", value, onChange, placeholder, testid }) => (
  <div>
    <Label htmlFor={id} className="text-sm text-[var(--ra-muted)]">{label}</Label>
    <Input id={id} type={type} value={value} required onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="mt-1.5 h-11 rounded-md border-[var(--ra-border)] focus-visible:ring-[var(--ra-primary)]" data-testid={testid} />
  </div>
);

const AuthShell = ({ title, subtitle, children }) => (
  <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
    <div className="hidden lg:flex flex-col justify-between p-12 bg-[var(--ra-primary)] text-white relative overflow-hidden">
      <div className="absolute inset-0 mudcloth-bg" />
      <Link to="/" className="flex items-center gap-2.5 relative" data-testid="auth-brand">
        <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center"><Lantern size={18} glow /></div>
        <div className="leading-tight">
          <span className="font-heading font-bold text-lg block">ClearPurse</span>
          <span className="text-[10px] uppercase tracking-[0.18em] text-white/60">The clear way home</span>
        </div>
      </Link>
      <div className="relative">
        <div className="text-xs uppercase tracking-[0.2em] text-[var(--ra-accent)] font-semibold mb-3">Two continents · One family</div>
        <h2 className="font-heading text-4xl font-bold leading-tight max-w-md">"My mom received my transfer before her tea got cold."</h2>
        <p className="text-white/70 mt-4 max-w-md">— Amara, sends to Lagos every month</p>
      </div>
      <div className="relative text-sm text-white/60">© 2026 Clearway Money</div>
    </div>

    <div className="flex items-center justify-center p-6 sm:p-10">
      <div className="w-full max-w-md fade-up">
        <h1 className="font-heading text-3xl font-bold tracking-tight">{title}</h1>
        <p className="text-[var(--ra-muted)] mt-1.5 mb-7">{subtitle}</p>
        {children}
      </div>
    </div>
  </div>
);
